import React, { useEffect, useRef } from "react"
import { animate } from "framer-motion"

interface CalculatorDisplayProps {
  totalHoursSaved: number
}

const AnimatedNumber = ({ value }: { value: number }) => {
  const nodeRef = useRef<HTMLSpanElement>(null)
  const previousValue = useRef(0)

  useEffect(() => {
    const node = nodeRef.current
    if (node) {
      const controls = animate(previousValue.current, value, {
        duration: 0.5,
        ease: "easeOut",
        onUpdate(latest) {
          node.textContent = Math.round(latest).toLocaleString()
        },
      })
      previousValue.current = value
      return () => controls.stop()
    }
  }, [value])

  return <span ref={nodeRef}>{Math.round(previousValue.current).toLocaleString()}</span>
}

export default function CalculatorDisplay({ totalHoursSaved }: CalculatorDisplayProps) {
  const daysSaved = totalHoursSaved / 24
  const monthsSavedIn10Years = (totalHoursSaved * 10) / (24 * 30.4375) // Avg days in month

  return (
    <div className="sticky top-28 p-6 md:p-8 bg-slate-800/50 backdrop-blur-md rounded-xl shadow-2xl text-white">
      <h3 className="text-2xl font-semibold mb-2 text-brand-teal">Your Potential Time Savings:</h3>

      <div className="my-6">
        <p className="text-gray-300 text-lg mb-1">Total Hours Saved Annually</p>
        <div className="text-6xl md:text-7xl font-bold text-brand-teal">
          <AnimatedNumber value={totalHoursSaved} />
        </div>
      </div>

      <div className="my-6">
        <p className="text-gray-300 text-sm mb-1">That's over</p>
        <p className="text-3xl font-semibold">
          <AnimatedNumber value={daysSaved} /> Days a year
        </p>
      </div>

      <div className="my-6">
        <p className="text-gray-300 text-sm mb-1">In 10 years, you could reclaim</p>
        <p className="text-3xl font-semibold">
          <AnimatedNumber value={monthsSavedIn10Years} /> months of your life
        </p>
      </div>
      <p className="text-xs text-gray-400 mt-8">*Calculations are approximate based on average time savings.</p>
    </div>
  )
}
